/**
 * View + paging model behind the Gameplay graph (GHS5-99). Pure: it turns the
 * play-by-day payload into "which buttons exist, which 7 days are on screen,
 * and what does each line plot".
 *
 * Kept out of the component so the boundary arithmetic (which is where the
 * promotion-week semantics actually live) can be exercised on its own.
 */
import type { PlayByDay, PlayWeekSeries } from './api/admin';
import type { ChartSeries } from '../components/admin/LineChart';
import { addDays, dateRange, dateRangeLabel, daySpan, daysBetween, shortDate, weekdayShort, weekdayWithDate } from './dayRange';

// Distinguishable on the dark admin surface; indexed by week so a week keeps
// its color across views.
const SERIES_COLORS = [
  '#f87171', // red
  '#fbbf24', // amber
  '#34d399', // emerald
  '#60a5fa', // blue
  '#c084fc', // purple
  '#f472b6', // pink
  '#2dd4bf', // teal
  '#a3e635', // lime
];

export interface ChartPage {
  dates: string[];
  label: string;
}

export interface ResolvedView {
  id: string;
  label: string;
  /** 'overlay' puts every contest week on one shared Friday-to-Thursday axis;
   *  'single' walks one week's full window a page at a time. */
  kind: 'overlay' | 'single';
  weeks: PlayWeekSeries[];
  pages: ChartPage[];
}

export function colorFor(weekNumber: number): string {
  return SERIES_COLORS[(weekNumber - 1) % SERIES_COLORS.length]!;
}

/**
 * Pages of (up to) 7 day-slots for a week whose window runs longer than one
 * promotion week.
 */
export function buildPages(week: PlayWeekSeries, today: string): ChartPage[] {
  const lastDate = week.endDate < today ? week.endDate : today;
  if (daysBetween(week.startDate, lastDate) < 0) return []; // hasn't started

  if (week.isFreePlay) {
    // Free Play's cutoff is an open-ended placeholder, so anchoring on the end
    // would re-slice every page as "today" moves. Run forward from day one
    // instead, which pins the pages to fixed Friday-to-Thursday boundaries.
    const pages: ChartPage[] = [];
    for (let start = week.startDate; daysBetween(start, lastDate) >= 0; start = addDays(start, 7)) {
      const end = addDays(start, 6);
      pages.push({ dates: dateRange(start, end), label: dateRangeLabel(start, end) });
    }
    return pages;
  }

  // A contest week anchors on the END of its window, so the newest page is
  // exactly the Friday-to-Thursday promotion week and older pages step back
  // from it. Only the oldest page can come up short — Week 1's window opens
  // when the contest does, which isn't a Friday.
  const pageCount = Math.max(1, Math.ceil(daySpan(week.startDate, week.endDate) / 7));
  return Array.from({ length: pageCount }, (_, p) => {
    const end = addDays(week.endDate, -(pageCount - 1 - p) * 7);
    const rawStart = addDays(end, -6);
    const start = rawStart < week.startDate ? week.startDate : rawStart;
    return { dates: dateRange(start, end), label: dateRangeLabel(start, end) };
  });
}

export function buildViews(data: PlayByDay): ResolvedView[] {
  const contestWeeks = data.weeks.filter((w) => !w.isFreePlay);
  const views: ResolvedView[] = [];

  // The overlay only needs an axis, so borrow the first contest week's promo
  // dates for the weekday labels; every contest week runs Friday to Thursday.
  const reference = contestWeeks[0];
  if (reference) {
    views.push({
      id: 'promo',
      label: 'Promotion weeks',
      kind: 'overlay',
      weeks: contestWeeks,
      pages: [
        {
          dates: dateRange(reference.promoStartDate, reference.promoEndDate),
          label: 'Friday – Thursday',
        },
      ],
    });
  }

  // A week whose window runs longer than a single promotion week gets its own
  // paged view: Week 1 (the contest opens weeks before Episode 1 airs) and Free
  // Play (open-ended).
  for (const week of data.weeks) {
    const oversized = daySpan(week.startDate, week.endDate) > 7;
    if (!week.isFreePlay && !oversized) continue;
    views.push({
      id: `week-${week.weekNumber}`,
      label: week.isFreePlay ? week.label : `${week.label} (full)`,
      kind: 'single',
      weeks: [week],
      pages: buildPages(week, data.today),
    });
  }

  return views;
}

/**
 * Series for one metric over the given view + page. A day the API didn't send
 * is a day that hasn't happened, so it becomes a null (a gap), never a zero.
 */
export function buildSeries(
  view: ResolvedView,
  page: ChartPage,
  metric: 'runs' | 'players',
): ChartSeries[] {
  if (view.kind === 'overlay') {
    return view.weeks
      .filter((w) => w.days.length > 0)
      .map((week) => {
        const byDate = new Map(week.days.map((d) => [d.date, d]));
        return {
          key: `week-${week.weekNumber}`,
          label: week.label,
          color: colorFor(week.weekNumber),
          points: page.dates.map((_, i) => {
            // Each week reads its OWN promo dates at this slot, not the
            // reference week's.
            const date = addDays(week.promoStartDate, i);
            const day = byDate.get(date);
            return { y: day ? day[metric] : null, sub: `${week.label} · ${shortDate(date)}` };
          }),
        };
      });
  }

  const week = view.weeks[0]!;
  const byDate = new Map(week.days.map((d) => [d.date, d]));
  return [
    {
      key: `week-${week.weekNumber}`,
      label: week.label,
      color: colorFor(week.weekNumber),
      points: page.dates.map((date) => {
        const day = byDate.get(date);
        return { y: day ? day[metric] : null, sub: week.label };
      }),
    },
  ];
}

/** Overlaid weeks share a weekday axis; a single week's own dates are unambiguous. */
export function xLabelsFor(view: ResolvedView, page: ChartPage): string[] {
  return view.kind === 'overlay' ? page.dates.map(weekdayShort) : page.dates.map(weekdayWithDate);
}
